import * as Sentry from '@sentry/react';

// Initialize Sentry for error tracking and performance monitoring
export const initSentry = () => {
  // Check if we're in production or have a DSN configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  if (dsn) {
    Sentry.init({
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration({
          tracePropagationTargets: ['localhost', /^https:\/\/planner\.usrtriton\.nl\/api/, /^\//],
        }),
      ],
      // Performance monitoring
      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.1,

      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',

      // Before sending to Sentry, filter out certain errors
      beforeSend(event, hint) {
        // Don't send errors in development unless specifically enabled
        if (environment === 'development' && !import.meta.env.VITE_SENTRY_DEBUG) {
          return null;
        }

        // Filter out common non-critical errors
        if (event.exception) {
          const error = hint.originalException;
          if (error && error.message) {
            // Skip network errors that are expected
            if (
              error.message.includes('NetworkError') ||
              error.message.includes('Failed to fetch') ||
              error.message.includes('ERR_NAME_NOT_RESOLVED')
            ) {
              return null;
            }

            // Skip React devtools errors
            if (error.message.includes('__REACT_DEVTOOLS_GLOBAL_HOOK__')) {
              return null;
            }
          }
        }

        return event;
      },

      // Add user context automatically
      initialScope: {
        tags: {
          component: 'frontend',
          framework: 'react',
        },
      },
    });

    console.log(`Sentry initialized for ${environment} environment`);
  } else {
    console.log('Sentry DSN not configured, error tracking disabled');
  }
};

// Custom error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Function to manually capture errors
export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    tags: {
      section: 'manual_capture',
      ...context.tags,
    },
    extra: context,
  });
};

// Function to capture messages (for logging)
export const captureMessage = (message, level = 'info', context = {}) => {
  Sentry.captureMessage(message, level, {
    tags: {
      section: 'manual_message',
      ...context.tags,
    },
    extra: context,
  });
};

// Function to set user context
export const setUserContext = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  // Compute user role based on new role system
  const userRole = user?.is_admin
    ? 'admin'
    : user?.training_group_memberships?.some((membership) => membership.role_in_group === 'coach')
      ? 'coach'
      : 'rower';

  Sentry.setUser({
    id: user.id,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    role: userRole,
  });
};

// Function to add breadcrumb
export const addBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

// Performance monitoring utilities
export const startTransaction = (name, op = 'navigation') => {
  return Sentry.startSpan({ name, op });
};

export const measureFunction = (fn, name) => {
  return Sentry.withScope((scope) => {
    scope.setTag('function', name);
    return fn();
  });
};
